import { useEffect, useState, useRef } from 'react';
import { CloudOff, Check, Loader2 } from 'lucide-react';
import { subscribe } from '../../lib/saveStatus';

/**
 * Indicatore discreto di salvataggio mostrato in basso a sinistra.
 *
 * Stati:
 *  - idle:    nascosto (default)
 *  - saving:  spinner + "Salvataggio…"
 *  - saved:   check verde + "Salvato" → svanisce dopo 1.5s
 *  - error:   icona errore + messaggio → resta finché non parte un nuovo save
 *
 * Si appoggia al bus event-based `lib/saveStatus.js` alimentato dagli
 * interceptors axios in `lib/api.js`.
 */
export default function SaveIndicator() {
  const [state, setState] = useState('idle'); // idle | saving | saved | error
  const [message, setMessage] = useState('');
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const unsub = subscribe(({ state: s, message: m }) => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (s === 'saving') {
        setState('saving');
        setMessage('');
      } else if (s === 'saved') {
        setState('saved');
        setMessage('');
        hideTimerRef.current = setTimeout(() => setState('idle'), 1500);
      } else if (s === 'error') {
        setState('error');
        setMessage(m || 'Salvataggio fallito');
        // l'errore non auto-svanisce: resta finché il prossimo save lo sostituisce
      }
    });
    return () => {
      unsub();
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  if (state === 'idle') return null;

  let icon = null;
  let label = '';
  let cls = '';
  if (state === 'saving') {
    icon = <Loader2 size={12} strokeWidth={1.5} className="animate-spin" />;
    label = 'Salvataggio…';
    cls = 'border-line text-soft bg-canvas';
  } else if (state === 'saved') {
    icon = <Check size={12} strokeWidth={2} />;
    label = 'Salvato';
    cls = 'border-ok/40 text-ok bg-ok/10';
  } else {
    icon = <CloudOff size={12} strokeWidth={1.5} />;
    label = message || 'Salvataggio fallito';
    cls = 'border-accent/40 text-accent bg-risk/10';
  }

  return (
    <div
      className={
        'fixed bottom-4 left-4 z-40 inline-flex items-center gap-1.5 px-3 py-1.5 border ' +
        'font-mono text-[10px] uppercase tracking-[0.18em] shadow-sm pointer-events-none ' +
        'transition-opacity duration-200 ' +
        cls
      }
      role="status"
      aria-live="polite"
      data-testid="save-indicator"
      data-state={state}
    >
      {icon}
      <span data-testid="save-indicator-label">{label}</span>
    </div>
  );
}
